import BaseView from '../../base-view';
import SuggestEvents from '../../events/suggest';
import Collection from './collection';
import ListView from './list/view';

export default BaseView.extend({
	minChars: 2,
	allowAnyValue: false,

	events: {
		'input input[type="search"]': 'onSearchInput',
		'blur input[type="search"]': 'onSearchBlur',
		'keydown input[type="search"]': 'onSearchKeyDown', // Use keydown to catch the 'special' key events
		'click .js-suggest-clear': 'onClearClick'
	},

	initialize: function(options) {
		// reference elements
		this.searchInput = this.query('input[type="search"]');
		this.valueInput = this.query('.js-suggest-value');
		this.btnClear = this.query('.js-suggest-clear');

		// create collection
		this.collection = new Collection();
		this.collection.url = options.dataUrl;
		this.allowAnyValue = this.el.getAttribute('data-allow-any') || false;

		// listeners
		this.listenTo(this.collection, 'change:IsSelected', this.selectItem.bind(this));

		// create subviews
		this.createSubviews();

		// toggle clear button
		this.toggleClearButton(!this.searchInput.value);
	},

	/**
	 * create subviews
	 **/
	createSubviews: function() {
		this.list = new ListView({
			collection: this.collection
		}).render();
	},

	/**
	 * focus on input handler
	 **/
	onSearchFocus: function() {
		// show list if searchterm meets min characters
		if (this.searchInput.value.length >= this.minChars) {
			this.showList();
		} else {
			this.hideList();
		}
	},

	/**
	 * blur input hander, hides the list
	 * use a small delay to enable clicks on result items
	 * otherwise the list is hidden before the user clicks on an item
	 **/
	onSearchBlur: function() {
		if (this.allowAnyValue) {
			this.setHiddenValue(this.searchInput.value);
		}

		setTimeout(function() {
			this.hideList();
			this.trigger(SuggestEvents.BLUR);
		}.bind(this), 350);
	},

	/**
	 * input handler for search field
	 **/
	onSearchInput: function(e) {
		// if term is long enough, search and show list
		if (this.searchInput.value.length >= this.minChars) {
			this.search(this.searchInput.value);
			this.showList();
		} else {
			this.clearHiddenValue();
			this.hideList();
		}
	},

	/**
	 * keydown handler
	 * used for up/down, escape, enter
	 **/
	onSearchKeyDown: function(e) {
		var key = e.originalEvent.key || e.originalEvent.keyIdentifier; // IE + FF or Chrome

		switch (key) {
			case 'Down':
			case 'ArrowDown':
				this.highlightNext();
				break;
			case 'Up':
			case 'ArrowUp':
				this.highlightPrevious();
				break;
			case 'Esc':
			case 'Escape':
			case 'U+001B':
				this.cancelSuggest();
				break;
			case 'Enter':
				this.selectHighlighted();
				break;
			default:
				return;
		}

		e.preventDefault();
	},

	/**
	 * cancel suggest
	 **/
	cancelSuggest: function() {
		this.emptyInput();
		this.setHiddenValue('');
		this.hideList();
		this.toggleClearButton(true);

		this.trigger(SuggestEvents.CANCEL);
	},

	/**
	 * alias for cancelSuggest
	 **/
	clear: function() {
		this.cancelSuggest();
	},

	/**
	 * clear button handler
	 **/
	onClearClick: function(e) {
		e.preventDefault();
		this.clear();
	},

	/**
	 * show the list
	 **/
	showList: function() {
		this.listIsShown = true;
		this.list.show();
	},

	/**
	 * hide the list
	 **/
	hideList: function() {
		this.listIsShown = false;
		this.list.hide();
	},

	/**
	 * highlight next item in list
	 **/
	highlightNext: function() {
		if (this.list.collection.length) {
			this.list.collection.highlightNext();
		}
	},

	/**
	 * highlight previous item in list
	 **/
	highlightPrevious: function() {
		if (this.list.collection.length) {
			this.list.collection.highlightPrevious();
		}
	},

	/**
	 * select the current highlighted item
	 **/
	selectHighlighted: function() {
		var highlighted = this.list.collection.getHighlighted();

		if (highlighted) {
			this.list.collection.setSelected(highlighted);
		} else {
			if (this.allowAnyValue) {
				this.setHiddenValue(this.searchInput.value);
			}
		}
	},

	/**
	 * Select an item from the list
	 **/
	selectItem: function() {
		// get the selected model
		var selected = this.list.collection.getSelected(),
			id = selected.get('Id'),
			name = selected.get('Name');

		// fill input
		this.searchInput.value = name;

		// fill hidden field with value (also id for now)
		this.setHiddenValue(id);

		// trigger select event
		this.trigger(SuggestEvents.SELECTED, id);

		// show clear button
		this.toggleClearButton(false);

		// hide the list
		this.hideList();
	},

	/**
	 * empty the input field
	 **/
	emptyInput: function() {
		this.searchInput.setAttribute('value', '');
		this.searchInput.value = '';
	},

	/**
	 * Fill hidden field with value
	 **/
	setHiddenValue: function(value) {
		this.valueInput.value = value;
		this.valueInput.setAttribute('value', value);
	},

	clearHiddenValue: function() {
		this.valueInput.value = '';
		this.valueInput.setAttribute('value', '');
	},

	/**
	 * perform a search
	 **/
	search: function(term) {
		this.trigger(SuggestEvents.SEARCHING);

		this.collection.fetch({
			reset: true,
			data: {
				q: term
			}
		});
	},

	/**
	 * update URL for collection to fetch data from
	 **/
	setDataUrl: function(url) {
		this.collection.url = url;

		// clear the list by resetting collection
		this.collection.reset();
	},

	/**
	 * show / hide clear button
	 **/
	toggleClearButton: function(hide) {
		if (!this.btnClear) {
			return;
		}

		this.btnClear.classList.toggle('is-hidden', hide);
	},

	render: function() {
		// append the list to the view
		this.el.appendChild(this.list.el);

		return this;
	},

	destroy: function() {
		this.undelegateEvents();
		this.list.remove();
	}
});
